/* ==========================================================================
   Essentials Vault — theme.js
   Entry point. Custom-element registry + global helpers.
   ========================================================================== */

(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // Header — measure height into CSS var; transparent overlay on homepage hero
  // -------------------------------------------------------------------------
  const header = document.querySelector('[data-header]');
  const root = document.documentElement;

  const announcement = document.querySelector('.announcement-bar');

  function syncHeights() {
    if (header) root.style.setProperty('--header-h', header.offsetHeight + 'px');
    if (announcement) root.style.setProperty('--announcement-h', announcement.offsetHeight + 'px');
    else root.style.setProperty('--announcement-h', '0px');
  }

  if (header || announcement) {
    syncHeights();
    window.addEventListener('resize', syncHeights);
  }

  if (header) {

    const mayBeTransparent = header.classList.contains('header--may-be-transparent');
    const isHomepage = document.body.classList.contains('template-index');
    const hero = mayBeTransparent && isHomepage ? document.querySelector('.hero-capsule') : null;

    if (hero) {
      document.body.classList.add('homepage-transparent-header');
    }

    function updateHeaderState() {
      header.toggleAttribute('data-scrolled', window.scrollY > 8);

      if (!hero) {
        header.removeAttribute('data-on-hero');
        return;
      }
      const heroBottom = hero.offsetTop + hero.offsetHeight - header.offsetHeight;
      if (window.scrollY < heroBottom) {
        header.setAttribute('data-on-hero', 'true');
      } else {
        header.removeAttribute('data-on-hero');
      }
    }

    updateHeaderState();
    window.addEventListener('scroll', updateHeaderState, { passive: true });
    window.addEventListener('resize', updateHeaderState);
  }

  // -------------------------------------------------------------------------
  // Mobile menu drawer toggle
  // -------------------------------------------------------------------------
  const menuToggle  = document.querySelector('[data-menu-toggle]');
  const menuDrawer  = document.querySelector('[data-menu-drawer]');
  const menuClose   = document.querySelector('[data-menu-close]');
  const menuOverlay = document.querySelector('[data-menu-overlay]');
  const MENU_FOCUSABLE = 'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function openMenu() {
    if (!menuDrawer) return;
    menuDrawer.setAttribute('data-open', 'true');
    menuDrawer.setAttribute('aria-hidden', 'false');
    menuToggle?.setAttribute('aria-expanded', 'true');
    menuOverlay?.setAttribute('data-visible', 'true');
    window.theme.lockScroll('menu');
    menuClose?.focus();
  }
  function closeMenu() {
    if (!menuDrawer) return;
    const wasOpen = menuDrawer.getAttribute('data-open') === 'true';
    menuDrawer.setAttribute('data-open', 'false');
    menuDrawer.setAttribute('aria-hidden', 'true');
    menuToggle?.setAttribute('aria-expanded', 'false');
    menuOverlay?.setAttribute('data-visible', 'false');
    window.theme.unlockScroll('menu');
    if (wasOpen) menuToggle?.focus();
  }
  function trapMenuFocus(e) {
    if (e.key !== 'Tab' || menuDrawer?.getAttribute('data-open') !== 'true') return;

    const focusable = Array.from(menuDrawer.querySelectorAll(MENU_FOCUSABLE))
      .filter((element) => element.getClientRects().length > 0);
    if (!focusable.length) return;

    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];
    const focusIsOutside = !menuDrawer.contains(document.activeElement);

    if (e.shiftKey && (document.activeElement === firstFocusable || focusIsOutside)) {
      e.preventDefault();
      lastFocusable.focus();
    } else if (!e.shiftKey && (document.activeElement === lastFocusable || focusIsOutside)) {
      e.preventDefault();
      firstFocusable.focus();
    }
  }
  menuToggle?.addEventListener('click', openMenu);
  menuClose?.addEventListener('click', closeMenu);
  menuOverlay?.addEventListener('click', closeMenu);
  menuDrawer?.addEventListener('keydown', trapMenuFocus);
  menuDrawer?.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('[data-locale-trigger]')) {
      closeMenu();
    }
  }, true);
  menuDrawer?.addEventListener('click', (e) => {
    if (e.target === menuDrawer) closeMenu();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { closeMenu(); closeCart(); }
  });

  // -------------------------------------------------------------------------
  // Cart drawer toggle (basic open/close — content rendered in Fáze 3)
  // -------------------------------------------------------------------------
  const cartTrigger = document.querySelector('[data-cart-trigger]');
  const cartDrawer  = document.querySelector('[data-cart-drawer]');
  const cartOverlay = document.querySelector('[data-cart-overlay]');
  const cartClose   = document.querySelector('[data-cart-close]');

  function openCart() {
    if (!cartDrawer) return;
    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }
    cartDrawer.setAttribute('data-open', 'true');
    cartOverlay?.setAttribute('data-visible', 'true');
    window.theme.lockScroll('cart');
  }
  function closeCart() {
    if (!cartDrawer) return;
    cartDrawer.setAttribute('data-open', 'false');
    cartOverlay?.setAttribute('data-visible', 'false');
    window.theme.unlockScroll('cart');
  }
  cartTrigger?.addEventListener('click', (e) => { e.preventDefault(); openCart(); });
  cartClose?.addEventListener('click', closeCart);
  cartOverlay?.addEventListener('click', closeCart);

  // -------------------------------------------------------------------------
  // Deferred hover-image hydration (ESS-135)
  // Secondary product-card images render with data-src/data-srcset and a
  // transparent placeholder so touch devices never download them. On
  // hover-capable (desktop) devices we PREFETCH them via IntersectionObserver
  // as each card approaches the viewport (rootMargin), so the image is already
  // in cache by the time the cursor arrives — the hover swap stays instant.
  // Touch devices (hover: none) never set up the observer → zero hover-image
  // weight. Off-screen cards stay deferred until they scroll near.
  // -------------------------------------------------------------------------
  (function () {
    function hydrate(img) {
      if (!img || img.dataset.hydrated) return;
      const card = img.closest('.card-product');
      // Keep the packshot visible until a real secondary photo is available.
      const ready = () => card?.classList.toggle('card-product--hover-ready', img.naturalWidth > 1);
      img.addEventListener('load', ready);
      img.addEventListener('error', () => card?.classList.remove('card-product--hover-ready'));
      if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
      if (img.dataset.src) img.setAttribute('src', img.dataset.src);
      img.dataset.hydrated = '1';
      if (img.complete) ready();
    }

    // Wire deferred hover images inside `scope` (default: whole document).
    // Exposed on window so cards injected client-side AFTER this initial pass
    // (e.g. recently-viewed, fetched via the Section Rendering API) can hydrate
    // the secondary images they add — otherwise those images keep data-src with
    // no src and the hover swap reveals a blank frame. Idempotent: skips
    // already-hydrated images and bails on non-hover (touch) devices.
    function wireHoverImages(scope) {
      if (!window.matchMedia('(hover: hover)').matches) return;
      scope = scope || document;
      const deferred = scope.querySelectorAll('.card-product__img--secondary[data-src]:not([data-hydrated])');
      if (!deferred.length) return;

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              hydrate(entry.target);
              obs.unobserve(entry.target);
            }
          });
        }, { rootMargin: '300px' });
        deferred.forEach((img) => io.observe(img));
      } else {
        // No IO support: hydrate all (still desktop-only, so acceptable).
        deferred.forEach(hydrate);
      }

      // Belt-and-suspenders: if the cursor reaches a card before the observer
      // has hydrated it (fast scroll + hover), promote its image immediately.
      scope.querySelectorAll('.card-product').forEach((card) => {
        card.addEventListener('pointerenter', () => {
          hydrate(card.querySelector('.card-product__img--secondary[data-src]'));
        }, { once: true });
      });
    }

    window.EssentialsVault = window.EssentialsVault || {};
    window.EssentialsVault.wireHoverImages = wireHoverImages;

    wireHoverImages(document);
    // Linked color navigation replaces related cards without rerunning this asset.
    document.addEventListener('pdp:content-updated', (event) => {
      if (event.detail?.root) wireHoverImages(event.detail.root);
    });
  })();

  // -------------------------------------------------------------------------
  // Collection hero video — recover when mobile autoplay is blocked
  // -------------------------------------------------------------------------
  document.querySelectorAll('[data-collection-video-hero]').forEach((hero) => {
    const video = hero.querySelector('.collection-video-hero__video');
    const playButton = hero.querySelector('[data-collection-video-play]');
    if (!video || !playButton) return;

    function showPlayFallback() {
      hero.removeAttribute('data-video-user-initiated');
      hero.setAttribute('data-video-paused', 'true');
      playButton.hidden = false;
    }

    function hidePlayFallback() {
      hero.removeAttribute('data-video-paused');
      playButton.hidden = true;
    }

    function playVideo(userInitiated) {
      if (userInitiated) {
        hero.setAttribute('data-video-user-initiated', 'true');
      }

      let playback;
      try {
        playback = video.play();
      } catch (_error) {
        showPlayFallback();
        return;
      }

      if (playback && typeof playback.then === 'function') {
        playback.then(hidePlayFallback).catch(showPlayFallback);
      } else if (video.paused) {
        showPlayFallback();
      } else {
        hidePlayFallback();
      }
    }

    playButton.addEventListener('click', () => playVideo(true));
    video.addEventListener('playing', hidePlayFallback);
    video.addEventListener('error', showPlayFallback);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.pause();
      showPlayFallback();
    } else {
      playVideo(false);
    }
  });

  // -------------------------------------------------------------------------
  // Locale switcher hint (placeholder; real switcher in Fáze 4)
  // -------------------------------------------------------------------------
})();
